import { useEffect } from "react";
import { createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Banner } from "fumadocs-ui/components/banner";
import { RootProvider } from "fumadocs-ui/provider/tanstack";

import appCss from "../styles.css?url";
import {
  GTM_HEAD_SCRIPT,
  INTERCOM_LOADER_SCRIPT,
  POSTHOG_API_HOST,
  POSTHOG_TOKEN,
} from "../lib/analytics";
import { SITE_DESCRIPTION, SITE_TITLE } from "../lib/site";

type RootHeadContext = {
  matches: {
    routeId: string;
  }[];
};

function isDocumentationPage(ctx: RootHeadContext): boolean {
  return ctx.matches.some((match) => match.routeId === "/$");
}

export const Route = createRootRoute({
  head: (ctx) => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: SITE_TITLE,
      },
      ...(isDocumentationPage(ctx)
        ? []
        : [{ name: "description", content: SITE_DESCRIPTION }]),
      {
        name: "theme-color",
        content: "#FFFFFF",
        media: "(prefers-color-scheme: light)",
      },
      {
        name: "theme-color",
        content: "#0A0A0A",
        media: "(prefers-color-scheme: dark)",
      },
    ],
    links: [
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="flex min-h-screen flex-col">
        <RootProvider i18n={{ translations: { search: "Search..." } }}>
          <Banner
            id="autoevals-announcement"
            variant="normal"
            height="43px"
            className="docs-announcement"
          >
            <span>
              Check out the newest way to compare different models for a
              task/agent harness:
            </span>{" "}
            <a
              href="/get-started/compare-models-auto-evals"
              className="docs-announcement-link"
            >
              AutoEvals
            </a>
          </Banner>
          <Analytics />
          {children}
        </RootProvider>
        <Scripts />
      </body>
    </html>
  );
}

// Gated on the live hostname, not just the build mode: PR previews and the
// development worker are production vite builds and must not report analytics.
const ANALYTICS_HOSTNAME = "docs.inference.net";

function Analytics() {
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (window.location.hostname !== ANALYTICS_HOSTNAME) return;

    void import("posthog-js").then(({ default: posthog }) => {
      posthog.init(POSTHOG_TOKEN, { api_host: POSTHOG_API_HOST });
    });

    for (const source of [GTM_HEAD_SCRIPT, INTERCOM_LOADER_SCRIPT]) {
      const script = document.createElement("script");
      script.textContent = source;
      document.head.appendChild(script);
    }
  }, []);

  return null;
}
